import { useEffect, useRef, useState } from "react";

const MIRE_MEDIA_LOGO = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-28/prod_agent_29681c11-d6f4-4494-92b6-9967ba534da1/mire_media_logo_b80c218352bb4f83b44633aa500aaeaa.png";
const MIRE_DRAMA_LOGO = "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-28/prod_agent_29681c11-d6f4-4494-92b6-9967ba534da1/mire_drama_logo_0a9f9404afd847c4b25252ced73e99f1.png";

const QR_GRID = [
  1,1,1,1,1,
  1,0,1,0,1,
  1,1,0,1,1,
  1,0,0,0,1,
  1,1,1,1,1,
  0,1,0,1,0,
  1,0,1,0,1,
];

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  r: number; alpha: number;
}

function ParticleCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animId: number;
    const particles: Particle[] = [];
    const COUNT = 60;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 2 + 1,
        alpha: Math.random() * 0.5 + 0.15,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,28,53,${p.alpha})`;
        ctx.fill();
      });
      for (let i = 0; i < COUNT; i++) {
        for (let j = i + 1; j < COUNT; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(255,28,53,${0.12 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return (
    <canvas ref={ref} style={{ position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />
  );
}

function Glitch({ text }: { text: string }) {
  return <span className="glitch" data-text={text}>{text}</span>;
}

function IEmail() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  );
}
function IPhone() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );
}
function IPin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}
function IGlobe() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
}
function IWA() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
  );
}
function ICopy() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}
function IShare() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );
}
function IDl() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function ContactRow({ icon, label, value, href }: {
  icon: React.ReactNode; label: string; value: string; href?: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const row = (
    <div className="crow">
      <div className="cicon">{icon}</div>
      <div className="ctext">
        <span className="clabel">{label}</span>
        <span className="cvalue">{value}</span>
      </div>
      <button className="ccopy" onClick={handleCopy}>{copied ? "✓" : <ICopy />}</button>
    </div>
  );
  return href ? <a href={href} style={{ textDecoration: "none" }}>{row}</a> : row;
}

export default function Index() {
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), 100); return () => clearTimeout(t); }, []);

  const saveVcard = () => {
    const vcf = [
      "BEGIN:VCARD","VERSION:3.0",
      "FN:PangGe (石约)","N:石约;PangGe;;;",
      "ORG:MIRE Group;MIRE Media","TITLE:Chairman",
      "EMAIL:PangGe@miregroup.ai",
      "TEL;TYPE=CELL:+601122121999",
      "ADR;TYPE=WORK:;;1802 VIDA BUKIT CEYLON\\, JALAN CEYLON;KUALA LUMPUR;;;MALAYSIA",
      "URL:https://miregroup.ai","END:VCARD",
    ].join("\n");
    const blob = new Blob([vcf], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "PangGe_MIRE.vcf"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    if (navigator.share) { navigator.share({ title: "PangGe – MIRE Group", url: window.location.href }); }
    else { navigator.clipboard.writeText(window.location.href); }
  };

  const CSS = `
    @import url("https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;900&family=Noto+Sans+SC:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap");
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    body{background:#08080d;min-height:100vh;font-family:'JetBrains Mono',monospace;overflow-x:hidden}
    .bg{position:fixed;inset:0;z-index:0;
      background:
        radial-gradient(ellipse 80% 50% at 20% 20%,rgba(255,28,53,.13) 0%,transparent 60%),
        radial-gradient(ellipse 60% 40% at 80% 80%,rgba(255,28,53,.09) 0%,transparent 55%),
        #08080d}
    .scanlines{position:fixed;inset:0;z-index:1;pointer-events:none;
      background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,.03) 2px,rgba(0,0,0,.03) 4px)}
    .page{position:relative;z-index:10;min-height:100vh;display:flex;flex-direction:column;
      align-items:center;justify-content:center;padding:32px 16px}
    .card{width:100%;max-width:520px;
      background:rgba(255,255,255,.03);backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);
      border:1px solid rgba(255,28,53,.28);border-radius:20px;padding:32px 28px;
      position:relative;overflow:hidden;
      opacity:0;transform:translateY(32px) scale(.97);
      transition:opacity .6s ease,transform .6s ease}
    .card.show{opacity:1;transform:translateY(0) scale(1)}
    .card::before{content:'';position:absolute;top:-80px;left:50%;transform:translateX(-50%);
      width:60%;height:160px;background:radial-gradient(ellipse,rgba(255,28,53,.18) 0%,transparent 70%);pointer-events:none}
    .card::after{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;
      background:linear-gradient(90deg,transparent,rgba(255,28,53,.8),transparent)}
    .card-hdr{display:flex;align-items:center;gap:10px;margin-bottom:28px}
    .lbadge{background:rgba(255,28,53,.08);border:1px solid rgba(255,28,53,.22);border-radius:8px;
      padding:6px 10px;display:flex;align-items:center}
    .lbadge img{height:24px;width:auto;display:block;object-fit:contain}
    .lsep{width:1px;height:28px;background:rgba(255,28,53,.25);margin:0 4px}
    .name-sec{margin-bottom:16px}
    .glitch{font-family:'Orbitron',sans-serif;font-size:30px;font-weight:900;letter-spacing:4px;
      text-transform:uppercase;color:#fff;display:inline-block;position:relative}
    .glitch::before,.glitch::after{content:attr(data-text);position:absolute;top:0;left:0;
      font-family:'Orbitron',sans-serif;font-size:30px;font-weight:900;letter-spacing:4px;text-transform:uppercase}
    .glitch::before{color:#FF1C35;text-shadow:-2px 0 #FF1C35;animation:gr 3.5s infinite linear;
      clip-path:polygon(0 0,100% 0,100% 45%,0 45%)}
    .glitch::after{color:#0ff;text-shadow:2px 0 #0ff;animation:gl 2.8s infinite linear;
      clip-path:polygon(0 55%,100% 55%,100% 100%,0 100%)}
    @keyframes gr{0%,94%{transform:none;opacity:0}95%{transform:translate(2px,-1px);opacity:.6}
      96%{transform:translate(-2px,1px);opacity:.4}97%{transform:translate(1px,2px);opacity:.6}98%,100%{transform:none;opacity:0}}
    @keyframes gl{0%,88%{transform:none;opacity:0}89%{transform:translate(-3px,1px);opacity:.5}
      90%{transform:translate(2px,-1px);opacity:.3}91%,100%{transform:none;opacity:0}}
    .name-cn{font-family:'Noto Sans SC',sans-serif;font-size:13px;letter-spacing:8px;
      color:rgba(255,255,255,.42);margin-top:6px}
    .roles{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:24px}
    .badge{display:inline-flex;align-items:center;gap:6px;font-family:'Orbitron',sans-serif;
      font-size:10px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:#fff;
      background:rgba(255,28,53,.1);border:1px solid rgba(255,28,53,.45);border-radius:20px;padding:5px 12px}
    .badge-cn{font-family:'Noto Sans SC',sans-serif;font-size:11px;font-weight:500;letter-spacing:2px}
    .bdot{width:6px;height:6px;border-radius:50%;background:#FF1C35;animation:pulse 2s infinite;flex-shrink:0}
    @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.7)}}
    .div{height:1px;background:linear-gradient(90deg,transparent,rgba(255,28,53,.3),transparent);margin:0 0 20px}
    .contacts{display:flex;flex-direction:column;gap:4px;margin-bottom:12px}
    .crow{display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;
      cursor:pointer;transition:background .2s;position:relative}
    .crow:hover{background:rgba(255,28,53,.07)}
    .crow:hover .ccopy{opacity:1}
    .cicon{width:34px;height:34px;border-radius:8px;background:rgba(255,28,53,.12);
      border:1px solid rgba(255,28,53,.25);display:flex;align-items:center;justify-content:center;
      color:#FF1C35;flex-shrink:0}
    .ctext{flex:1;min-width:0}
    .clabel{display:block;font-family:'Orbitron',sans-serif;font-size:9px;font-weight:500;
      letter-spacing:1.5px;color:rgba(255,28,53,.8);text-transform:uppercase;margin-bottom:2px}
    .cvalue{display:block;font-family:'JetBrains Mono',monospace;font-size:13px;
      color:rgba(255,255,255,.88);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .ccopy{opacity:0;background:rgba(255,28,53,.15);border:1px solid rgba(255,28,53,.3);
      border-radius:6px;color:rgba(255,255,255,.7);padding:4px 6px;cursor:pointer;
      font-size:11px;transition:opacity .2s;flex-shrink:0}
    .wa-btn{display:flex;align-items:center;gap:12px;padding:12px 16px;border-radius:12px;
      background:rgba(37,211,102,.06);border:1px solid rgba(37,211,102,.35);
      text-decoration:none;cursor:pointer;margin-bottom:4px;transition:background .2s}
    .wa-btn:hover{background:rgba(37,211,102,.12)}
    .wa-icon{width:36px;height:36px;border-radius:50%;background:rgba(37,211,102,.15);
      display:flex;align-items:center;justify-content:center;color:#25D366;flex-shrink:0}
    .wa-text{flex:1}
    .wa-lbl{display:block;font-family:'Orbitron',sans-serif;font-size:9px;font-weight:500;
      letter-spacing:1.5px;color:#25D366;text-transform:uppercase;margin-bottom:2px}
    .wa-val{display:block;font-family:'JetBrains Mono',monospace;font-size:13px;color:rgba(255,255,255,.88)}
    .wa-arrow{color:rgba(37,211,102,.6);font-size:18px}
    .card-foot{display:flex;align-items:flex-end;justify-content:space-between;margin-top:20px;margin-bottom:20px}
    .slogan{font-family:'Orbitron',sans-serif;font-size:8px;font-weight:400;letter-spacing:1.5px;
      color:rgba(255,255,255,.22);text-transform:uppercase;line-height:1.6}
    .qr{display:grid;grid-template-columns:repeat(5,8px);grid-template-rows:repeat(7,8px);gap:2px}
    .qon{width:8px;height:8px;border-radius:1px;background:#FF1C35}
    .qoff{width:8px;height:8px;border-radius:1px;background:rgba(255,28,53,.1)}
    .acts{display:flex;gap:10px}
    .btn-save{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;padding:13px 0;
      background:linear-gradient(135deg,#FF1C35 0%,#c0001a 100%);border:none;border-radius:12px;
      font-family:'Orbitron',sans-serif;font-size:11px;font-weight:700;letter-spacing:2px;
      text-transform:uppercase;color:#fff;cursor:pointer;transition:filter .2s,transform .1s}
    .btn-save:hover{filter:brightness(1.1);transform:translateY(-1px)}
    .btn-save:active{transform:translateY(0)}
    .btn-share{width:48px;height:48px;display:flex;align-items:center;justify-content:center;
      background:rgba(255,28,53,.08);border:1px solid rgba(255,28,53,.35);border-radius:12px;
      color:rgba(255,255,255,.7);cursor:pointer;transition:background .2s;flex-shrink:0}
    .btn-share:hover{background:rgba(255,28,53,.15)}
    .credit{font-family:'Orbitron',sans-serif;font-size:8px;letter-spacing:2px;
      color:rgba(255,255,255,.15);text-align:center;margin-top:20px;text-transform:uppercase}
  `;

  return (
    <>
      <style>{CSS}</style>
      <div className="bg" />
      <div className="scanlines" />
      <ParticleCanvas />
      <main className="page">
        <div className={`card${vis ? " show" : ""}`}>
          {/* Logos */}
          <div className="card-hdr">
            <div className="lbadge"><img src={MIRE_MEDIA_LOGO} alt="MIRE MEDIA" /></div>
            <div className="lsep" />
            <div className="lbadge"><img src={MIRE_DRAMA_LOGO} alt="MIRE DRAMA" /></div>
          </div>

          {/* Name */}
          <div className="name-sec">
            <div><Glitch text="PANGGE" /></div>
            <div className="name-cn">石 &nbsp;·&nbsp; 约</div>
          </div>

          {/* Role badges */}
          <div className="roles">
            <span className="badge"><span className="bdot" />CHAIRMAN</span>
            <span className="badge badge-cn"><span className="bdot" />董事长</span>
          </div>

          <div className="div" />

          {/* Contacts */}
          <div className="contacts">
            <ContactRow icon={<IEmail />} label="EMAIL" value="PangGe@miregroup.ai" href="mailto:PangGe@miregroup.ai" />
            <ContactRow icon={<IPhone />} label="PHONE" value="+60 11-2212 1999" href="tel:+601122121999" />
            <ContactRow icon={<IPin />}   label="ADDRESS" value="1802, VIDA BUKIT CEYLON, JALAN CEYLON, KL" />
            <ContactRow icon={<IGlobe />} label="WEBSITE" value="miregroup.ai" href="https://miregroup.ai" />
          </div>

          {/* WhatsApp */}
          <a href="https://wa.me/601122121999" target="_blank" rel="noopener noreferrer" className="wa-btn">
            <div className="wa-icon"><IWA /></div>
            <div className="wa-text">
              <span className="wa-lbl">WHATSAPP</span>
              <span className="wa-val">+60 11-2212 1999</span>
            </div>
            <span className="wa-arrow">→</span>
          </a>

          {/* Footer */}
          <div className="card-foot">
            <div className="slogan">NEXT GENERATION<br />AI ENTERTAINMENT PLATFORM</div>
            <div className="qr">
              {QR_GRID.map((on, i) => (
                <div key={i} className={on ? "qon" : "qoff"} />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="acts">
            <button className="btn-save" onClick={saveVcard}><IDl />SAVE CONTACT</button>
            <button className="btn-share" onClick={handleShare} title="Share"><IShare /></button>
          </div>
        </div>
        <p className="credit">© 2026 MIRE GROUP · ALL RIGHTS RESERVED</p>
      </main>
    </>
  );
}
